/**
 * R2: Cross-seed exact duplicate merge [AUTO-APPLY, confidence=1.0 when safe]
 *
 * Trigger: jak R1 ale dup-y w RÓŻNYCH seedach.
 * Target seed = seed z MAX(avg_jaccard) między dup.tokens a seed.tokens.
 *   Tie-break: MIN(priority) (core p=1 wygrywa z outer p=5).
 * Canonical: jeśli jeden z dupów już jest w target_seed → wygrywa (stabilność).
 *   W innych przypadkach: R1 tie-break (SV desc → incoming desc → slug len → slug ASC).
 * Action: UPDATE canonical.seed_id = target (jeśli trzeba) + R1-like rewire/delete.
 * Safety: którykolwiek dup ma matched_wp_post → SKIP.
 */

import type {
  RepairRuleInput,
  RepairRuleResult,
  MergeOp,
  SkippedOp,
  RepairSeedRow,
  RepairNodeRow,
} from "@/types/mapRepair";
import {
  pickCanonical,
  groupByEntityIntentCrossSeeds,
  nodeTokens,
  seedTokens,
  jaccardSimilarity,
} from "./_shared";

export function runR2(input: RepairRuleInput): RepairRuleResult {
  const operations: RepairRuleResult["operations"] = [];
  const skipped: SkippedOp[] = [];

  // Precompute: seedTokensById + nodesInSeed
  const nodesBySeed = new Map<string, RepairNodeRow[]>();
  for (const n of input.nodes) {
    const list = nodesBySeed.get(n.seed_id) || [];
    list.push(n);
    nodesBySeed.set(n.seed_id, list);
  }
  const seedTokensById = new Map<string, string[]>();
  const seedsById = new Map<string, RepairSeedRow>();
  for (const seed of input.seeds) {
    seedsById.set(seed.id, seed);
    const scopeNodes = nodesBySeed.get(seed.id) || [];
    seedTokensById.set(seed.id, seedTokens(seed, scopeNodes));
  }

  const groups = groupByEntityIntentCrossSeeds(input.nodes);

  for (const [key, nodesInGroup] of groups.entries()) {
    if (nodesInGroup.length < 2) continue;
    // R2 TYLKO cross-seed — jeśli wszystkie w tym samym seed, odpuszczamy (R1 zrobi)
    const seedIds = new Set(nodesInGroup.map((n) => n.seed_id));
    if (seedIds.size < 2) continue;

    // Safety — jakikolwiek dup ma matched_wp_post → skip całą grupę
    const hasMatch = nodesInGroup.find((n) => n.matched_wp_post_id);
    if (hasMatch) {
      for (const n of nodesInGroup) {
        skipped.push({
          node_id: n.id,
          slug: n.slug,
          rule: "R2",
          reason: `group_has_wp_post (matched: ${hasMatch.slug})`,
        });
      }
      continue;
    }

    // Target seed selection — per każdy kandydat seed liczymy avg_jaccard
    const candidateSeedIds = Array.from(seedIds);
    const jaccardBySeed: Record<string, number> = {};
    for (const sid of candidateSeedIds) {
      const sTokens = seedTokensById.get(sid) || [];
      let sum = 0;
      for (const n of nodesInGroup) {
        sum += jaccardSimilarity(nodeTokens(n), sTokens);
      }
      jaccardBySeed[sid] = nodesInGroup.length > 0 ? sum / nodesInGroup.length : 0;
    }

    // Target = MAX(avg_jaccard), tie-break MIN(priority)
    const ranked = candidateSeedIds
      .map((sid) => ({
        seedId: sid,
        j: jaccardBySeed[sid],
        priority: seedsById.get(sid)?.priority ?? 99,
      }))
      .sort((a, b) => {
        if (b.j !== a.j) return b.j - a.j;
        return a.priority - b.priority;
      });
    const targetSeedId = ranked[0].seedId;

    const canonical = pickCanonical(nodesInGroup, input.incomingByNodeId, {
      preferredSeedId: targetSeedId,
    });
    const deleted = nodesInGroup.filter((n) => n.id !== canonical.id);

    const [entity, intent] = key.split("|");

    // Move canonical do target_seed jeśli trzeba
    const needsMove = canonical.seed_id !== targetSeedId;
    const moveOp = needsMove
      ? {
          node_id: canonical.id,
          slug: canonical.slug,
          old_seed_id: canonical.seed_id,
          new_seed_id: targetSeedId,
        }
      : null;

    // Rewire plan — identyczny jak R1 (z duplicate detection)
    const deletedIds = new Set(deleted.map((n) => n.id));
    const affectedRelations = input.relations.filter(
      (r) => deletedIds.has(r.from_node_id) || deletedIds.has(r.to_node_id),
    );
    const affectedRelationIds = new Set(affectedRelations.map((r) => r.id));

    const relKey = (
      mapId: string,
      from: string,
      to: string,
      type: string | null,
    ) => `${mapId}|${from}|${to}|${type ?? ""}`;
    const existingKeys = new Set<string>();
    for (const r of input.relations) {
      if (!affectedRelationIds.has(r.id)) {
        existingKeys.add(relKey(r.map_id, r.from_node_id, r.to_node_id, r.relation_type));
      }
    }

    const rewireOps: Array<{
      relation_id: string;
      old_from: string;
      old_to: string;
      new_from: string;
      new_to: string;
    }> = [];
    const selfLoopRelationIds: string[] = [];
    const redundantDuplicateIds: string[] = [];

    for (const r of affectedRelations) {
      const newFrom = deletedIds.has(r.from_node_id)
        ? canonical.id
        : r.from_node_id;
      const newTo = deletedIds.has(r.to_node_id) ? canonical.id : r.to_node_id;

      if (newFrom === newTo) {
        selfLoopRelationIds.push(r.id);
        continue;
      }

      const newKey = relKey(r.map_id, newFrom, newTo, r.relation_type);
      if (existingKeys.has(newKey)) {
        redundantDuplicateIds.push(r.id);
        continue;
      }

      existingKeys.add(newKey);
      rewireOps.push({
        relation_id: r.id,
        old_from: r.from_node_id,
        old_to: r.to_node_id,
        new_from: newFrom,
        new_to: newTo,
      });
    }

    const mergeOp: MergeOp = {
      canonical: {
        id: canonical.id,
        slug: canonical.slug,
        seed_id: targetSeedId,
      },
      deleted: deleted.map((n) => ({
        id: n.id,
        slug: n.slug,
        seed_id: n.seed_id,
      })),
      entity,
      intent,
      relations_rewired: rewireOps.length,
      self_loops_removed: selfLoopRelationIds.length,
      target_seed_id: targetSeedId,
      avg_jaccard: jaccardBySeed,
    };

    operations.push({
      operation: "merge",
      payload: {
        rule: "R2",
        merge: mergeOp,
        move: moveOp,
        rewire: rewireOps,
        self_loops: selfLoopRelationIds,
        redundant_duplicates: redundantDuplicateIds,
      },
      rollback_payload: {
        deleted_nodes: deleted.map((n) => ({ ...n })),
        moved_node: moveOp
          ? {
              node_id: canonical.id,
              old_seed_id: moveOp.old_seed_id,
            }
          : null,
        rewired_relations: affectedRelations.map((r) => ({
          id: r.id,
          from_node_id: r.from_node_id,
          to_node_id: r.to_node_id,
          relation_type: r.relation_type,
          weight: r.weight,
          map_id: r.map_id,
        })),
      },
    });
  }

  return {
    rule: "R2",
    operations,
    proposals: [],
    flags: [],
    skipped,
  };
}
